// src/reducers/user.js

import {
  ADD_USER_TO_BRAND,
  ADD_NEW_BRAND,
  UPDATE_IFC_BRAND,
  SET_COMPANY_USERS,
  SET_BRANDS,
  SET_USER,
  REMOVE_USER_FROM_BRAND,
  REMOVE_BRAND,
  SET_STATUSES,
  SET_PERM,
  FEATURE_REQUEST,
  SET_SIDEBAR,
  SET_CATALOGS_TREE,
  SET_CATALOGS,
  SET_COMPANY_PERMISSION,
  SET_ALL_PERMISSION,
  SET_ALL_HEALTH_REPORT,
  SET_ALL_PARAMETERS,
  SET_COMPANY_SETTINGS,
  UPDATE_BRAND,
  SET_PERSONAL_AREA_ACTIVE_TAB,
  SET_COMPANY_ROLE,
  SET_USER_ANALYTICS,
  REFRESH_BRANDS,
  BRAND_FILES_TREE,
  SET_ADMIN_MOBILE_SIDEBAR,
  SET_ACTIVE_BRAND_CONTEXT,
  RESET_BRAND_ACTIVE_CONTEXT,
  UPSERT_BRAND,
  SET_BRAND_SETTINGS,
  PATCH_BRAND_SETTINGS,
  UPSERT_SOCIAL_ACCOUNT,
  PATCH_SOCIAL_ACCOUNT,
  REMOVE_SOCIAL_ACCOUNT,
  UPSERT_SOCIAL_TARGET,
  PATCH_SOCIAL_TARGET,
  REMOVE_SOCIAL_TARGET,
  SET_DEFAULT_SOCIAL_TARGET,
  SET_BRAND_POSTS,
  APPEND_BRAND_POSTS,
  SET_BRAND_IDEAS,
  APPEND_BRAND_IDEAS,

} from "../constants/action-types/user";

import { APPLY_CONNECT_PAYLOAD } from "constants/action-types/social";



/* ======================
   Defaults & Helpers
   ====================== */

const BRAND_SETTINGS_DEFAULT = {
  _id: null,
  socialAccounts: [],
  socialTargets: [],
  adsAccounts: [],
  byPlatform: {},
  platforms: [],
  selectedPlatforms: [],
  selectedBundle: { platforms: [], byPlatform: {} },
  selectedByPlatform: {},
  postingType: "text",
  postingTime: null,
  postingCadence: "everyday",
};

/** Build byPlatform + platforms + selectedPlatforms from the flat arrays + server mapping */
function deriveByPlatform(bs = {}) {
  const selectedByPlatform = bs.selectedByPlatform || {};
  const out = {}; // { [platform]: { accounts[], targets[], adsAccounts[], selected:{accountId,targetId,adsId} } }

  function ensure(platform) {
    const p = String(platform || "").toLowerCase();
    if (!out[p]) {
      out[p] = {
        accounts: [],
        targets: [],
        adsAccounts: [],
        selected: { accountId: null, targetId: null, adsId: null },
      };
    }
    return p;
  }

  // accounts
  (bs.socialAccounts || []).forEach((a) => {
    const p = ensure(a && a.platform);
    const selectedMap = selectedByPlatform[p] || {};
    const isSel =
      !!a?.selected || String(selectedMap.accountId || "") === String(a?._id || "");
    out[p].accounts.push({ ...(a || {}), selected: !!isSel });
    if (isSel && a?._id != null) out[p].selected.accountId = String(a._id);
  });

  // targets
  (bs.socialTargets || []).forEach((t) => {
    const p = ensure(t && t.platform);
    const tid = String((t && (t._id || (t.externalId && `${p}:${t.externalId}`))) || "");
    const selectedMap = selectedByPlatform[p] || {};
    const isSel =
      !!t?.selected ||
      !!t?.isDefault ||
      String(selectedMap.targetId || "") === tid;
    out[p].targets.push({
      ...(t || {}),
      _id: tid,
      selected: !!isSel,
      isDefault: !!isSel || !!t?.isDefault,
    });
    if (isSel) out[p].selected.targetId = tid;
  });

  // ads
  (bs.adsAccounts || []).forEach((ad) => {
    const p = ensure(ad && ad.platform);
    const selectedMap = selectedByPlatform[p] || {};
    const isSel =
      !!ad?.selected || String(selectedMap.adsId || "") === String(ad?._id || "");
    out[p].adsAccounts.push({ ...(ad || {}), selected: !!isSel });
    if (isSel && ad?._id != null) out[p].selected.adsId = String(ad._id);
  });

  const platforms = Object.keys(out);
  const selectedPlatforms = platforms.filter((p) => {
    const s = out[p].selected || {};
    return !!(s.accountId || s.targetId || s.adsId);
  });

  return { byPlatform: out, platforms, selectedPlatforms };
}

/** Update the selected booleans inside byPlatform for one platform */
function applySelectionToByPlatform(byPlatform = {}, platform, { accountId, targetId, adsId }) {
  const p = String(platform || "").toLowerCase();
  const existing = byPlatform[p] || {};
  const bin = {
    accounts: Array.isArray(existing.accounts) ? existing.accounts.slice() : [],
    targets: Array.isArray(existing.targets) ? existing.targets.slice() : [],
    adsAccounts: Array.isArray(existing.adsAccounts) ? existing.adsAccounts.slice() : [],
    selected: { ...(existing.selected || {}) },
  };

  const markSelected = (arr, id) =>
    Array.isArray(arr) ? arr.map((x) => ({ ...x, selected: String(x._id || x.id) === String(id) })) : [];

  if (accountId != null) {
    bin.accounts = markSelected(bin.accounts, accountId);
    bin.selected.accountId = String(accountId);
  }
  if (targetId != null) {
    bin.targets = bin.targets.map((t) => {
      const sel = String(t._id) === String(targetId);
      return { ...t, selected: sel, isDefault: sel || !!t.isDefault };
    });
    bin.selected.targetId = String(targetId);
  }
  if (adsId != null) {
    bin.adsAccounts = markSelected(bin.adsAccounts, adsId);
    bin.selected.adsId = String(adsId);
  }

  return { ...byPlatform, [p]: bin };
}

/** If server gave byPlatform, synthesize selectedByPlatform from it (DB ids) */
function selectedMapFromByPlatform(byPlatform = {}) {
  const map = {};
  Object.keys(byPlatform || {}).forEach((p) => {
    const sel = (byPlatform[p] && byPlatform[p].selected) || {};
    if (sel.accountId || sel.targetId || sel.adsId) {
      map[p] = {};
      if (sel.accountId) map[p].accountId = String(sel.accountId);
      if (sel.targetId) map[p].targetId = String(sel.targetId);
      if (sel.adsId) map[p].adsId = String(sel.adsId);
    }
  });
  return map;
}

// user reducer helpers
function patchSettingsBranch(brand, brandSettingsId, updater) {
  // If settings are an array, find and patch the exact one.
  if (Array.isArray(brand.brandSettings)) {
    const idx = brand.brandSettings.findIndex(
      (s) => String(s?._id) === String(brandSettingsId)
    );
    if (idx === -1) return brand; // nothing to do
    const nextArr = brand.brandSettings.slice();
    nextArr[idx] = updater({ ...(nextArr[idx] || {}), _id: nextArr[idx]?._id });
    return { ...brand, brandSettings: nextArr };
  }

  // Single object branch
  const bs = brand.brandSettings || {};
  const idMatches =
    !brandSettingsId || (bs?._id && String(bs._id) === String(brandSettingsId));
  if (!idMatches && brandSettingsId) return brand;

  return { ...brand, brandSettings: updater({ ...bs, _id: bs._id || brandSettingsId }) };
}

const idOf = (x) => (x && (x._id || x.id)) || x;

const upsertItem = (arr = [], item, key = "_id") => {
  if (!item) return arr.slice();
  const k = item[key];
  const idx = arr.findIndex((it) => String(it[key]) === String(k));
  if (idx === -1) return [...arr, item];
  const next = arr.slice();
  next[idx] = { ...arr[idx], ...item };
  return next;
};

const removeItem = (arr = [], id, key = "_id") =>
  (arr || []).filter((it) => String(it[key]) !== String(id));

/** Safely normalize a brand while preserving all server fields and extending brandSettings inside the array */
const ensureBrandShape = (raw = {}) => {
  // start from server payload to avoid data loss
  const b = { ...raw };

  // Always ensure _id exists and is stringifiable
  b._id = idOf(raw);

  // Normalize arrays the component expects
  b.users = Array.isArray(raw.users) ? raw.users.slice() : [];
  b.posts = Array.isArray(raw.posts) ? raw.posts.slice() : [];
  b.ideas = Array.isArray(raw.ideas) ? raw.ideas.slice() : [];

  // Handle brandSettings: could be an ID (string) or an object
  let brandSettingsId = null;
  let brandSettingsObj = {};

  if (raw.brandSettings && typeof raw.brandSettings === "object" && !Array.isArray(raw.brandSettings)) {
    brandSettingsObj = { ...raw.brandSettings };
  }
  if (brandSettingsObj._id) brandSettingsId = String(brandSettingsObj._id);
  else if (typeof raw.brandSettings === "string") {
    brandSettingsId = raw.brandSettings; // keep the ID
  }

  // Accept legacy top-level social arrays too
  const topSocialAccounts = Array.isArray(raw.socialAccounts) ? raw.socialAccounts : [];
  const topSocialTargets = Array.isArray(raw.socialTargets) ? raw.socialTargets : [];
  const topAdsAccounts = Array.isArray(raw.adsAccounts) ? raw.adsAccounts : [];

  // Seed defaults, then overlay server, then fill arrays from either branch
  let bs = {
    ...BRAND_SETTINGS_DEFAULT,
    ...(brandSettingsObj || {}),
    socialAccounts: Array.isArray(brandSettingsObj.socialAccounts)
      ? brandSettingsObj.socialAccounts.slice()
      : topSocialAccounts.slice(),
    socialTargets: Array.isArray(brandSettingsObj.socialTargets)
      ? brandSettingsObj.socialTargets.slice()
      : topSocialTargets.slice(),
    adsAccounts: Array.isArray(brandSettingsObj.adsAccounts)
      ? brandSettingsObj.adsAccounts.slice()
      : topAdsAccounts.slice(),
  };

  // Derive/repair per-platform structure for THIS brand item
  const derived = deriveByPlatform({
    ...bs,
    selectedByPlatform: bs.selectedByPlatform,
  });

  // Prefer server fields if present, otherwise fill from derived
  bs.byPlatform = bs.byPlatform && Object.keys(bs.byPlatform).length ? bs.byPlatform : derived.byPlatform;
  bs.platforms =
    Array.isArray(bs.platforms) && bs.platforms.length ? bs.platforms : derived.platforms;
  bs.selectedPlatforms =
    Array.isArray(bs.selectedPlatforms) && bs.selectedPlatforms.length
      ? bs.selectedPlatforms
      : derived.selectedPlatforms;

  // Ensure selectedByPlatform is present (prefer server, otherwise synthesize from byPlatform)
  bs.selectedByPlatform =
    bs.selectedByPlatform && Object.keys(bs.selectedByPlatform).length
      ? bs.selectedByPlatform
      : selectedMapFromByPlatform(bs.byPlatform);

  b.brandSettings = bs;
  if (brandSettingsId) b.brandSettingsId = brandSettingsId;

  // Normalize nested ids on posts/ideas
  b.posts = (b.posts || []).map((p) => ({ ...(p || {}), _id: idOf(p) }));
  b.ideas = (b.ideas || []).map((i) => ({ ...(i || {}), _id: idOf(i) }));

  return b;
};

/* Initial State */
const initialState = {
  activeBrandId: null,
  activeBrandSettingsId: null,

  userInfo: null,
  brands: [],

  // other UI/legacy state left as-is
  users: null,
  userPermission: [],
  allPosts: [],
  featureRequest: null,
  sidebar: false,
  adminSidebar: false,
  companyPermission: [],
  allPermission: [],
  companySettings: null,
  personalAreaActiveTab: "CARD_VIEW",
  companyRoles: null,
  userAnalytics: null,
  refreshBrands: false,
  statuses: undefined,
  healthReports: undefined,
  parameters: undefined,
  catalogsTree: undefined,
  catalogs: undefined,
  brandFilesTree: undefined,
};

function normalizeSetBrandsPayload(payload) {
  // Accept array or { brands: [...] }
  const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.brands) ? payload.brands : []);
  return list.map((raw) => {
    const brand = ensureBrandShape(raw);

    // Normalize socialAccounts platform/status if present
    brand.brandSettings.socialAccounts = (brand.brandSettings.socialAccounts || []).map((a) => {
      const platform = String(a?.platform || "").toLowerCase();
      let status = a?.status;
      if (!status) {
        const looksConnected =
          !!a?.accessToken ||
          !!a?.refreshToken ||
          (Array.isArray(a?.pages) && a.pages.length > 0);
        status = looksConnected ? "connected" : "disconnected";
      }
      return { ...(a || {}), _id: String(idOf(a)), platform, status };
    });

    // (optional) derive socialTargets from accounts.defaultTarget/targets and merge
    const derivedTargets = [];
    (brand.brandSettings.socialAccounts || []).forEach((acc) => {
      const platform = (acc.platform || "").toLowerCase();
      if (acc?.defaultTarget?.id) {
        derivedTargets.push({
          _id: `${platform}:${acc.defaultTarget.id}`,
          platform,
          externalId: String(acc.defaultTarget.id),
          name: acc.defaultTarget.name,
          type: acc.defaultTarget.type,
          avatarUrl: acc.defaultTarget.avatarUrl,
          isDefault: true,
          account: String(acc._id),
          brand_id: String(brand._id),
        });
      }
      if (Array.isArray(acc?.targets)) {
        acc.targets.forEach((t) => {
          const tid =
            idOf(t) ||
            (t?.externalId && `${platform}:${t.externalId}`) ||
            (t?.id && `${platform}:${t.id}`);
        if (!tid) return;
          derivedTargets.push({ ...t, _id: tid, platform, account: String(acc._id) });
        });
      }
    });

    const mergedTargets = new Map();
    [...(brand.brandSettings.socialTargets || []), ...derivedTargets].forEach((t) => {
      const tid = String(idOf(t));
      mergedTargets.set(tid, { ...(mergedTargets.get(tid) || {}), ...t, _id: tid });
    });
    brand.brandSettings.socialTargets = Array.from(mergedTargets.values());

    // Final derivation to ensure byPlatform/platforms/selectedPlatforms/selectedByPlatform are ready
    const d2 = deriveByPlatform({
      ...brand.brandSettings,
      selectedByPlatform: brand.brandSettings.selectedByPlatform,
    });
    brand.brandSettings.byPlatform =
      brand.brandSettings.byPlatform && Object.keys(brand.brandSettings.byPlatform).length
        ? brand.brandSettings.byPlatform
        : d2.byPlatform;
    brand.brandSettings.platforms =
      Array.isArray(brand.brandSettings.platforms) && brand.brandSettings.platforms.length
        ? brand.brandSettings.platforms
        : d2.platforms;
    brand.brandSettings.selectedPlatforms =
      Array.isArray(brand.brandSettings.selectedPlatforms) && brand.brandSettings.selectedPlatforms.length
        ? brand.brandSettings.selectedPlatforms
        : d2.selectedPlatforms;
    brand.brandSettings.selectedByPlatform =
      brand.brandSettings.selectedByPlatform && Object.keys(brand.brandSettings.selectedByPlatform).length
        ? brand.brandSettings.selectedByPlatform
        : selectedMapFromByPlatform(brand.brandSettings.byPlatform);

    return brand;
  });
}

/* Reducer */
export default function userReducer(state = initialState, action) {
  switch (action.type) {
    case SET_ACTIVE_BRAND_CONTEXT: {
      const { brandId, brandSettingsId } = action.payload || {};
      return {
        ...state,
        activeBrandId: brandId ?? state.activeBrandId,
        activeBrandSettingsId: brandSettingsId ?? state.activeBrandSettingsId,
      };
    }
    case RESET_BRAND_ACTIVE_CONTEXT: {
      const { brandId, brandSettingsId } = action.payload || {};
      console.log("RESET_BRAND_ACTIVE_CONTEXT in reducer", brandId, brandSettingsId);
      return {
        ...state,
        activeBrandId: brandId ?? state.activeBrandId,
        activeBrandSettingsId: brandSettingsId ?? state.activeBrandSettingsId,
      };
    }
    case "UPDATE_BRAND_SOCIAL_ACCOUNT": {
      const { brandId, account, targets, brandSettingsId } = action.payload;

      return {
        ...state,
        brands: state.brands.map((brand) => {
          if (String(brand._id) !== String(brandId)) return brand;

          return patchSettingsBranch(brand, brandSettingsId, (bs) => {
            const socialAccounts = Array.isArray(bs.socialAccounts) ? bs.socialAccounts : [];
            const socialTargets = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];

            const accountIndex = socialAccounts.findIndex(
              (a) => String(a._id) === String(account._id)
            );
            const updatedAccount = {
              ...account,
              status: account.status || "connected",
              lastUpdated: Date.now(),
            };
            const updatedAccounts =
              accountIndex >= 0
                ? socialAccounts.map((a, i) => (i === accountIndex ? updatedAccount : a))
                : [...socialAccounts, updatedAccount];

            const targetMap = new Map(
              socialTargets.map((t) => [`${t.platform}:${t.externalId}`, t])
            );
            (targets || []).forEach((t) => {
              const key = `${t.platform}:${t.externalId}`;
              targetMap.set(key, { ...t, _id: key, account: account._id });
            });

            const next = { ...bs, socialAccounts: updatedAccounts, socialTargets: Array.from(targetMap.values()) };

            // keep derived views coherent
            const d = deriveByPlatform(next);
            next.byPlatform =
              next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
            next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
            next.selectedPlatforms =
              next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
            next.selectedByPlatform =
              next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
                ? next.selectedByPlatform
                : selectedMapFromByPlatform(next.byPlatform);

            return next;
          });
        }),
      };
    }

    case "UPDATE_BRAND_SETTINGS_SELECTION": {
      const { brandId, brandSettingsId, platform, accountId, targetId, adsAccountId } =
        action.payload;

      return {
        ...state,
        brands: state.brands.map((brand) => {
          if (String(brand._id) !== String(brandId)) return brand;

          return patchSettingsBranch(brand, brandSettingsId, (bs) => {
            const selMap = bs.selectedByPlatform || {};
            const nextSel = { ...selMap };
            const p = String(platform || "").toLowerCase();
            const prev = selMap[p] || {};

            nextSel[p] = {
              ...prev,
              ...(accountId != null ? { accountId: String(accountId) } : {}),
              ...(targetId != null ? { targetId: String(targetId) } : {}),
              ...(adsAccountId != null ? { adsId: String(adsAccountId) } : {}),
              lastUpdated: Date.now(),
            };

            const nextByPlatform = applySelectionToByPlatform(bs.byPlatform || {}, platform, {
              accountId,
              targetId,
              adsId: adsAccountId != null ? adsAccountId : null,
            });

            const platforms = Object.keys(nextByPlatform);
            const selectedPlatforms = platforms.filter((pp) => {
              const s = (nextByPlatform[pp] && nextByPlatform[pp].selected) || {};
              return !!(s.accountId || s.targetId || s.adsId);
            });

            return {
              ...bs,
              selectedByPlatform: nextSel,
              byPlatform: nextByPlatform,
              platforms,
              selectedPlatforms,
            };
          });
        }),
      };
    }

    case "REMOVE_BRAND_SOCIAL_ACCOUNT": {
      const { brandId, platform, brandSettingsId } = action.payload;

      return {
        ...state,
        brands: state.brands.map((brand) => {
          if (String(brand._id) !== String(brandId)) return brand;
          return patchSettingsBranch(brand, brandSettingsId, (bs) => {
            const socialAccounts = Array.isArray(bs.socialAccounts) ? bs.socialAccounts : [];
            const socialTargets = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];
            const selectedByPlatform = bs.selectedByPlatform || {};

            const plat = String(platform || "").toLowerCase();
            const filteredAccounts = socialAccounts.filter(
              (a) => String(a.platform).toLowerCase() !== plat
            );
            const filteredTargets = socialTargets.filter(
              (t) => String(t.platform).toLowerCase() !== plat
            );

            const nextSel = { ...selectedByPlatform };
            if (nextSel[plat]) delete nextSel[plat];

            const next = {
              ...bs,
              socialAccounts: filteredAccounts,
              socialTargets: filteredTargets,
              selectedByPlatform: nextSel,
            };

            const d = deriveByPlatform(next);
            next.byPlatform = d.byPlatform;
            next.platforms = d.platforms;
            next.selectedPlatforms = d.selectedPlatforms;

            return next;
          });
        }),
      };
    }

    case "SET_BRAND_DEFAULT_TARGET": {
      // `targetId` is preferred the Mongo `_id` of the SocialTarget but
      // — for optimistic dispatches from a freshly-picked dropdown —
      // we accept the platform-external id too. `externalTargetId` /
      // `chosenName` are optional optimistic hints carried by the
      // SocialMediaConnect onChange so the row name updates before the
      // server round-trip completes and the real target doc lands.
      const { brandId, platform, accountId, targetId, externalTargetId, chosenName } = action.payload;

      return {
        ...state,
        brands: state.brands.map((brand) => {
          if (String(brand._id) !== String(brandId)) return brand;

          const bs = brand.brandSettings || {};
          const selectedByPlatform = bs.selectedByPlatform || {};
          const p = String(platform || "").toLowerCase();

          const nextSelected = {
            ...selectedByPlatform,
            [p]: {
              ...(selectedByPlatform[p] || {}),
              accountId,
              targetId,
              lastUpdated: Date.now(),
            },
          };

          // also flip flags within targets and defaultTarget of the account
          const targets = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];
          // Match the chosen target by EITHER mongo `_id` OR external id.
          // Optimistic dispatches from the dropdown often only know the
          // external id; without this fallback the row name wouldn't
          // refresh until the real save round-tripped.
          const _matchChosen = (t) =>
            (targetId && String(t._id) === String(targetId)) ||
            (externalTargetId && String(t.externalId) === String(externalTargetId));
          const nextTargets = targets.map((t) =>
            String(t.account) === String(accountId)
              ? { ...t, isDefault: _matchChosen(t), selected: _matchChosen(t) }
              : t
          );

          const accs = Array.isArray(bs.socialAccounts) ? bs.socialAccounts : [];
          let chosen = nextTargets.find(_matchChosen);
          // Last-resort synthetic chosen when the real SocialTarget doc
          // hasn't landed in Redux yet — keeps the row's "Target: X"
          // text reactive to the dropdown pick.
          if (!chosen && (externalTargetId || chosenName)) {
            chosen = {
              externalId: externalTargetId || targetId,
              name: chosenName || null,
              type: null,
              platform: p,
              avatarUrl: null,
            };
          }
          const nextAccs = accs.map((a) =>
            String(a._id) !== String(accountId)
              ? a
              : {
                  ...a,
                  defaultTarget: chosen
                    ? {
                        id: String(chosen.externalId || chosen._id || externalTargetId || targetId),
                        type: chosen.type,
                        name: chosen.name,
                        platform: chosen.platform || p,
                        avatarUrl: chosen.avatarUrl,
                        updatedAt: new Date().toISOString(),
                      }
                    : a.defaultTarget,
                }
          );

          // refresh derived views
          const next = {
            ...bs,
            socialTargets: nextTargets,
            socialAccounts: nextAccs,
            selectedByPlatform: nextSelected,
          };
          const d = deriveByPlatform(next);
          next.byPlatform = d.byPlatform;
          next.platforms = d.platforms;
          next.selectedPlatforms = d.selectedPlatforms;

          return { ...brand, brandSettings: next };
        }),
      };
    }

    case "ENSURE_BRAND_SETTINGS": {
      const { brandId, brandSettings } = action.payload || {};
      if (!brandId) return state;

      const brands = (state.brands || []).map((b) => {
        if (String(b._id) !== String(brandId)) return b;

        const bs = b.brandSettings || {};
        // keep existing arrays if present, otherwise initialize
        const next = {
          ...BRAND_SETTINGS_DEFAULT,
          ...bs,
          ...(brandSettings || {}),
          socialAccounts: Array.isArray(bs.socialAccounts) ? bs.socialAccounts : [],
          socialTargets: Array.isArray(bs.socialTargets) ? bs.socialTargets : [],
          adsAccounts: Array.isArray(bs.adsAccounts) ? bs.adsAccounts : [],
        };

        const d = deriveByPlatform(next);
        next.byPlatform = next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
        next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
        next.selectedPlatforms =
          next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
        next.selectedByPlatform =
          next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
            ? next.selectedByPlatform
            : selectedMapFromByPlatform(next.byPlatform);

        return { ...b, brandSettings: next };
      });

      return { ...state, brands };
    }

    case "MERGE_BRAND_SETTINGS": {
      const { brandId, brandSettings } = action.payload || {};
      if (!brandId || !brandSettings) return state;

      const incSA = Array.isArray(brandSettings.socialAccounts) ? brandSettings.socialAccounts : [];
      const incST = Array.isArray(brandSettings.socialTargets) ? brandSettings.socialTargets : [];
      const incADS = Array.isArray(brandSettings.adsAccounts) ? brandSettings.adsAccounts : [];

      const upsertLocal = (arr, item, key = "_id") => {
        const id = String(item && item[key] != null ? item[key] : "");
        if (!id) return arr;
        const i = arr.findIndex((x) => String(x && x[key]) === id);
        if (i === -1) return [...arr, item];
        const clone = arr.slice();
        clone[i] = { ...arr[i], ...item };
        return clone;
      };

      const brands = (state.brands || []).map((b) => {
        if (String(b._id) !== String(brandId)) return b;

        const bs = b.brandSettings || {};
        let sa = Array.isArray(bs.socialAccounts) ? bs.socialAccounts : [];
        let st = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];
        let ads = Array.isArray(bs.adsAccounts) ? bs.adsAccounts : [];

        incSA.forEach((a) => {
          sa = upsertLocal(sa, a);
        });
        incST.forEach((t) => {
          st = upsertLocal(st, t);
        });
        incADS.forEach((ad) => {
          ads = upsertLocal(ads, ad);
        });

        const next = {
          ...BRAND_SETTINGS_DEFAULT,
          ...bs,
          _id: String(bs._id || brandSettings._id || ""),
          brandId: String(brandId),
          socialAccounts: sa,
          socialTargets: st,
          adsAccounts: ads,
        };

        const d = deriveByPlatform(next);
        next.byPlatform =
          next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
        next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
        next.selectedPlatforms =
          next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
        next.selectedByPlatform =
          next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
            ? next.selectedByPlatform
            : selectedMapFromByPlatform(next.byPlatform);

        return { ...b, brandSettings: next };
      });

      return { ...state, brands };
    }

    /* ===== BRANDS: bulk set ===== */
    case SET_BRANDS: {
      const normalized = normalizeSetBrandsPayload(action.payload);
      return {
        ...state,
        brands: normalized,
        allPosts: normalized.flatMap((b) => b.posts || []),
      };
    }

    /* ===== BRAND upserts/removal ===== */
    case UPSERT_BRAND:
    case UPDATE_BRAND: {
      // The PUT /brands endpoint returns brandSettings.socialAccounts/
      // socialTargets/adsAccounts as bare ObjectId strings (no
      // population). If we blindly upsert the response over the
      // existing populated arrays, the row goes blank after every
      // Save because the SMA reads .platform / .defaultTarget /
      // .status off objects that no longer exist.
      // Defense: when the incoming arrays look like ID-only strings
      // OR are empty AND the existing brand had real docs, keep the
      // existing populated arrays.
      const incoming = ensureBrandShape(action.payload);
      const existingBrand = (state.brands || []).find((x) => String(x && x._id) === String(incoming._id));
      if (existingBrand && existingBrand.brandSettings) {
        const merged = { ...incoming };
        const incBs = incoming.brandSettings || {};
        const exBs  = existingBrand.brandSettings || {};
        const looksLikeIdsOnly = (arr) =>
          !Array.isArray(arr) || arr.length === 0 ||
          arr.every((x) => typeof x === 'string' || (x && !x.platform && !x.status));
        const preserved = { ...incBs };
        if (looksLikeIdsOnly(incBs.socialAccounts) && Array.isArray(exBs.socialAccounts) && exBs.socialAccounts.length > 0) {
          preserved.socialAccounts = exBs.socialAccounts;
        }
        if (looksLikeIdsOnly(incBs.socialTargets) && Array.isArray(exBs.socialTargets) && exBs.socialTargets.length > 0) {
          preserved.socialTargets = exBs.socialTargets;
        }
        if (looksLikeIdsOnly(incBs.adsAccounts) && Array.isArray(exBs.adsAccounts) && exBs.adsAccounts.length > 0) {
          preserved.adsAccounts = exBs.adsAccounts;
        }
        // Preserve byPlatform / selectedByPlatform / selectedPlatforms
        // if the incoming response doesn't carry them (the PUT
        // endpoint only returns the flat BrandSettings doc).
        if (!incBs.byPlatform && exBs.byPlatform) preserved.byPlatform = exBs.byPlatform;
        if (!incBs.selectedByPlatform && exBs.selectedByPlatform) preserved.selectedByPlatform = exBs.selectedByPlatform;
        if (!incBs.selectedPlatforms && exBs.selectedPlatforms) preserved.selectedPlatforms = exBs.selectedPlatforms;
        if (!incBs.platforms && exBs.platforms) preserved.platforms = exBs.platforms;
        merged.brandSettings = preserved;
        const next = upsertItem(state.brands, merged, "_id");
        return { ...state, brands: next, allPosts: next.flatMap((x) => x.posts || []) };
      }
      const next = upsertItem(state.brands, incoming, "_id");
      return { ...state, brands: next, allPosts: next.flatMap((x) => x.posts || []) };
    }

    case ADD_NEW_BRAND: {
      const b = ensureBrandShape(action.payload);
      const next = upsertItem(state.brands, b, "_id");
      return { ...state, brands: next };
    }

    case UPDATE_IFC_BRAND: {
      const b = ensureBrandShape(action.payload);
      const next = upsertItem(state.brands, b, "_id");
      return { ...state, brands: next };
    }

    case REMOVE_BRAND: {
      const brandId = String(action.payload);
      const next = removeItem(state.brands, brandId, "_id");
      return { ...state, brands: next, allPosts: next.flatMap((x) => x.posts || []) };
    }

    /* ===== BRAND SETTINGS ===== */
    case SET_BRAND_SETTINGS: {
      const { brandId, settings } = action.payload || {};
      const bid = String(brandId);
      const nextBrands = state.brands.map((b) => {
        if (String(b._id) !== bid) return b;

        const prev = b.brandSettings || {};
        const bs = {
          ...BRAND_SETTINGS_DEFAULT,
          ...prev,
          ...(settings || {}),
          socialAccounts: Array.isArray(settings?.socialAccounts)
            ? settings.socialAccounts.map((a) => ({ ...(a || {}), _id: String(idOf(a)) }))
            : (prev.socialAccounts || []),
          socialTargets: Array.isArray(settings?.socialTargets)
            ? settings.socialTargets.map((t) => ({ ...(t || {}), _id: String(idOf(t)) }))
            : (prev.socialTargets || []),
          adsAccounts: Array.isArray(settings?.adsAccounts)
            ? settings.adsAccounts.map((a) => ({ ...(a || {}), _id: String(idOf(a)) }))
            : (prev.adsAccounts || []),
        };

        const d = deriveByPlatform(bs);
        bs.byPlatform = bs.byPlatform && Object.keys(bs.byPlatform).length ? bs.byPlatform : d.byPlatform;
        bs.platforms = bs.platforms && bs.platforms.length ? bs.platforms : d.platforms;
        bs.selectedPlatforms =
          bs.selectedPlatforms && bs.selectedPlatforms.length ? bs.selectedPlatforms : d.selectedPlatforms;
        bs.selectedByPlatform =
          bs.selectedByPlatform && Object.keys(bs.selectedByPlatform).length
            ? bs.selectedByPlatform
            : selectedMapFromByPlatform(bs.byPlatform);

        return { ...b, brandSettings: bs };
      });
      return { ...state, brands: nextBrands };
    }

    case PATCH_BRAND_SETTINGS: {
      const { brandId, patch } = action.payload || {};
      const bid = String(brandId);
      const nextBrands = state.brands.map((b) => {
        if (String(b._id) !== bid) return b;
        const prev = b.brandSettings || {};
        const next = { ...prev, ...(patch || {}) };

        const d = deriveByPlatform(next);
        next.byPlatform =
          next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
        next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
        next.selectedPlatforms =
          next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
        next.selectedByPlatform =
          next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
            ? next.selectedByPlatform
            : selectedMapFromByPlatform(next.byPlatform);

        return { ...b, brandSettings: next };
      });
      return { ...state, brands: nextBrands };
    }

    /* ===== SOCIAL ACCOUNTS ===== */
    case UPSERT_SOCIAL_ACCOUNT: {
      const { brandId, record } = action.payload || {};
      const bid = String(brandId);
      const acc = { ...(record || {}), _id: String(idOf(record)) };

      const nextBrands = state.brands.map((b) => {
        if (String(b._id) !== bid) return b;
        const bs = b.brandSettings || { ...BRAND_SETTINGS_DEFAULT };

        const nextAccs = upsertItem(bs.socialAccounts || [], acc, "_id");
        const next = { ...bs, socialAccounts: nextAccs };

        const d = deriveByPlatform(next);
        next.byPlatform =
          next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
        next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
        next.selectedPlatforms =
          next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
        next.selectedByPlatform =
          next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
            ? next.selectedByPlatform
            : selectedMapFromByPlatform(next.byPlatform);

        return { ...b, brandSettings: next };
      });
      return { ...state, brands: nextBrands };
    }

    case PATCH_SOCIAL_ACCOUNT: {
      const { brandId, accountId, patch } = action.payload || {};
      const bid = String(brandId);
      const aid = String(accountId);

      const nextBrands = state.brands.map((b) => {
        if (String(b._id) !== bid) return b;
        const bs = b.brandSettings || { ...BRAND_SETTINGS_DEFAULT };
        const nextAccs = (bs.socialAccounts || []).map((a) =>
          String(a._id) === aid ? { ...a, ...(patch || {}) } : a
        );

        const next = { ...bs, socialAccounts: nextAccs };
        const d = deriveByPlatform(next);
        next.byPlatform =
          next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
        next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
        next.selectedPlatforms =
          next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
        next.selectedByPlatform =
          next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
            ? next.selectedByPlatform
            : selectedMapFromByPlatform(next.byPlatform);

        return { ...b, brandSettings: next };
      });
      return { ...state, brands: nextBrands };
    }

    case REMOVE_SOCIAL_ACCOUNT: {
      const { brandId, accountId } = action.payload || {};
      const bid = String(brandId);
      const aid = String(accountId);

      const nextBrands = state.brands.map((b) => {
        if (String(b._id) !== bid) return b;
        const bs = b.brandSettings || { ...BRAND_SETTINGS_DEFAULT };
        const nextAccs = removeItem(bs.socialAccounts || [], aid, "_id");
        const nextTargets = (bs.socialTargets || []).filter((t) => String(t.account) !== aid);

        const next = { ...bs, socialAccounts: nextAccs, socialTargets: nextTargets };
        const d = deriveByPlatform(next);
        next.byPlatform =
          next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
        next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
        next.selectedPlatforms =
          next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
        next.selectedByPlatform =
          next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
            ? next.selectedByPlatform
            : selectedMapFromByPlatform(next.byPlatform);

        return { ...b, brandSettings: next };
      });
      return { ...state, brands: nextBrands };
    }

    /* ===== SOCIAL TARGETS & DEFAULTS ===== */
    case UPSERT_SOCIAL_TARGET: {
      const { brandId, record, brandSettingsId } = action.payload || {};
      const bid = String(brandId);
      const rec = { ...(record || {}), _id: String(idOf(record)) };
      return {
        ...state,
        brands: state.brands.map((b) => {
          if (String(b._id) !== bid) return b;
          return patchSettingsBranch(b, brandSettingsId, (bs) => {
            const list = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];
            const nextTargets = upsertItem(list, rec, "_id");
            const next = { ...bs, socialTargets: nextTargets };

            const d = deriveByPlatform(next);
            next.byPlatform =
              next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
            next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
            next.selectedPlatforms =
              next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
            next.selectedByPlatform =
              next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
                ? next.selectedByPlatform
                : selectedMapFromByPlatform(next.byPlatform);

            return next;
          });
        }),
      };
    }

    case PATCH_SOCIAL_TARGET: {
      const { brandId, targetId, patch, brandSettingsId } = action.payload || {};
      const bid = String(brandId);
      const tid = String(targetId);
      return {
        ...state,
        brands: state.brands.map((b) => {
          if (String(b._id) !== bid) return b;
          return patchSettingsBranch(b, brandSettingsId, (bs) => {
            const list = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];
            const nextTargets = list.map((t) =>
              String(t._id) === tid ? { ...t, ...(patch || {}) } : t
            );
            const next = { ...bs, socialTargets: nextTargets };

            const d = deriveByPlatform(next);
            next.byPlatform =
              next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
            next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
            next.selectedPlatforms =
              next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
            next.selectedByPlatform =
              next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
                ? next.selectedByPlatform
                : selectedMapFromByPlatform(next.byPlatform);

            return next;
          });
        }),
      };
    }

    case REMOVE_SOCIAL_TARGET: {
      const { brandId, targetId, brandSettingsId } = action.payload || {};
      const bid = String(brandId);
      const tid = String(targetId);

      return {
        ...state,
        brands: state.brands.map((b) => {
          if (String(b._id) !== bid) return b;
          return patchSettingsBranch(b, brandSettingsId, (bs) => {
            const list = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];
            const nextTargets = removeItem(list, tid, "_id");
            const next = { ...bs, socialTargets: nextTargets };

            const d = deriveByPlatform(next);
            next.byPlatform =
              next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
            next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
            next.selectedPlatforms =
              next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
            next.selectedByPlatform =
              next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
                ? next.selectedByPlatform
                : selectedMapFromByPlatform(next.byPlatform);

            return next;
          });
        }),
      };
    }

    case SET_DEFAULT_SOCIAL_TARGET: {
      const { brandId, accountId, targetId, brandSettingsId } = action.payload || {};
      const bid = String(brandId);
      return {
        ...state,
        brands: state.brands.map((b) => {
          if (String(b._id) !== bid) return b;
          return patchSettingsBranch(b, brandSettingsId, (bs) => {
            const targets = Array.isArray(bs.socialTargets) ? bs.socialTargets : [];
            const nextTargets = targets.map((t) =>
              String(t.account) === String(accountId)
                ? { ...t, isDefault: String(t._id) === String(targetId), selected: String(t._id) === String(targetId) }
                : t
            );
            const chosen = nextTargets.find((t) => String(t._id) === String(targetId));
            const accs = Array.isArray(bs.socialAccounts) ? bs.socialAccounts : [];
            const nextAccs = accs.map((a) =>
              String(a._id) !== String(accountId)
                ? a
                : {
                    ...a,
                    defaultTarget: chosen
                      ? {
                          id: String(chosen.externalId || chosen._id),
                          type: chosen.type,
                          name: chosen.name,
                          platform: chosen.platform,
                          avatarUrl: chosen.avatarUrl,
                          updatedAt: new Date().toISOString(),
                        }
                      : a.defaultTarget,
                  }
            );

            const next = { ...bs, socialTargets: nextTargets, socialAccounts: nextAccs };

            const d = deriveByPlatform(next);
            next.byPlatform =
              next.byPlatform && Object.keys(next.byPlatform).length ? next.byPlatform : d.byPlatform;
            next.platforms = next.platforms && next.platforms.length ? next.platforms : d.platforms;
            next.selectedPlatforms =
              next.selectedPlatforms && next.selectedPlatforms.length ? next.selectedPlatforms : d.selectedPlatforms;
            next.selectedByPlatform =
              next.selectedByPlatform && Object.keys(next.selectedByPlatform).length
                ? next.selectedByPlatform
                : selectedMapFromByPlatform(next.byPlatform);

            return next;
          });
        }),
      };
    }

    /* ===== POSTS / IDEAS ===== */
    case SET_BRAND_POSTS: {
      const { brandId, posts } = action.payload || {};
      const bid = String(brandId);
      const nextBrands = state.brands.map((b) =>
        String(b._id) === bid
          ? { ...b, posts: (posts || []).map((p) => ({ ...(p || {}), _id: idOf(p) })) }
          : b
      );
      return { ...state, brands: nextBrands, allPosts: nextBrands.flatMap((x) => x.posts || []) };
    }

    case APPEND_BRAND_POSTS: {
      const { brandId, posts } = action.payload || {};
      const bid = String(brandId);
      const nextBrands = state.brands.map((b) => {
        if (String(b._id) !== bid) return b;
        const next = (b.posts || []).slice();
        (posts || []).forEach((p) => {
          const doc = { ...(p || {}), _id: idOf(p) };
          const idx = next.findIndex((x) => String(x._id) === String(doc._id));
          if (idx === -1) next.push(doc);
          else next[idx] = { ...next[idx], ...doc };
        });
        return { ...b, posts: next };
      });
      return { ...state, brands: nextBrands, allPosts: nextBrands.flatMap((x) => x.posts || []) };
    }

    case 'UPDATE_ALL_POSTS_STATUS': {
      // Mirrors the post-status drag into state.user.allPosts AND the
      // matching brand.posts entry so the AllPostsPage Kanban stays in
      // sync without a refetch. Both arrays get a new reference so React
      // re-renders; the per-post replacement is shallow-merged so the
      // rest of the post doc is preserved.
      const { postId, statusId, queueStatus } = action.payload || {};
      if (!postId) return state;
      const patch = {};
      if (typeof statusId === 'string') patch.status = statusId;
      if (typeof queueStatus === 'string') patch.queueStatus = queueStatus;
      const nextAllPosts = (state.allPosts || []).map((p) =>
        String(p && p._id) === String(postId) ? { ...p, ...patch } : p
      );
      const nextBrands = (state.brands || []).map((b) => {
        if (!Array.isArray(b.posts)) return b;
        let changed = false;
        const np = b.posts.map((p) => {
          if (String(p && p._id) !== String(postId)) return p;
          changed = true;
          return { ...p, ...patch };
        });
        return changed ? { ...b, posts: np } : b;
      });
      return { ...state, allPosts: nextAllPosts, brands: nextBrands };
    }

    case 'UPDATE_BRAND_POST': {
      // Mirrors a saved post EDIT into the big data structure:
      // state.user.brands[].posts (brand tree-view) and the derived
      // state.user.allPosts (AllPostsPage list / social / kanban).
      // UPDATE_POST only touches state.post.posts, so without this an
      // edit stayed invisible on those views until a refresh re-pulled
      // the brands. We locate the post by _id across every brand (no
      // reliance on brand_id being present on the edited doc), then
      // shallow-merge the returned fields onto the existing entry so any
      // normalized/extra fields already in the brand copy are preserved.
      // New array refs are handed back so React re-renders.
      const incoming = action.payload || {};
      const pid = String(idOf(incoming) || '');
      if (!pid) return state;
      let touched = false;
      const nextBrands = (state.brands || []).map((b) => {
        if (!Array.isArray(b.posts)) return b;
        let changed = false;
        const np = b.posts.map((p) => {
          if (String(p && p._id) !== pid) return p;
          changed = true;
          touched = true;
          return { ...(p || {}), ...incoming, _id: idOf(p) };
        });
        return changed ? { ...b, posts: np } : b;
      });
      if (!touched) return state;
      return { ...state, brands: nextBrands, allPosts: nextBrands.flatMap((x) => x.posts || []) };
    }

    case SET_BRAND_IDEAS: {
      const { brandId, ideas } = action.payload || {};
      const bid = String(brandId);
      const nextBrands = state.brands.map((b) =>
        String(b._id) === bid
          ? { ...b, ideas: (ideas || []).map((i) => ({ ...(i || {}), _id: idOf(i) })) }
          : b
      );
      return { ...state, brands: nextBrands };
    }

    case APPEND_BRAND_IDEAS: {
      const { brandId, ideas } = action.payload || {};
      const bid = String(brandId);
      const nextBrands = state.brands.map((b) => {
        if (String(b._id) !== bid) return b;
        const next = (b.ideas || []).slice();
        (ideas || []).forEach((i) => {
          const doc = { ...(i || {}), _id: idOf(i) };
          const idx = next.findIndex((x) => String(x._id) === String(doc._id));
          if (idx === -1) next.push(doc);
          else next[idx] = { ...next[idx], ...doc };
        });
        return { ...b, ideas: next };
      });
      return { ...state, brands: nextBrands };
    }

    /* ===== Misc passthroughs ===== */
    case SET_STATUSES:
      return { ...state, statuses: action.payload };
    case SET_COMPANY_USERS:
      return { ...state, users: action.payload };
    case "RMSHARE": {
      const b = ensureBrandShape(action.payload);
      return { ...state, brands: upsertItem(state.brands, b, "_id") };
    }
    case SET_USER:
      return { ...state, userInfo: action.payload };
    case SET_PERM:
      return { ...state, userPermission: action.payload };
    case SET_COMPANY_PERMISSION:
      return { ...state, companyPermission: action.payload };
    case SET_ALL_PERMISSION:
      return { ...state, allPermission: action.payload };
    case FEATURE_REQUEST:
      return { ...state, featureRequest: action.payload };
    case SET_SIDEBAR:
      return { ...state, sidebar: action.payload };
    case SET_ADMIN_MOBILE_SIDEBAR:
      return { ...state, adminSidebar: action.payload };
    case SET_PERSONAL_AREA_ACTIVE_TAB:
      return { ...state, personalAreaActiveTab: action.payload };
    case SET_ALL_HEALTH_REPORT:
      return { ...state, healthReports: action.payload };
    case SET_ALL_PARAMETERS:
      return { ...state, parameters: action.payload };
    case SET_CATALOGS_TREE:
      return { ...state, catalogsTree: action.payload };
    case SET_CATALOGS:
      return { ...state, catalogs: action.payload };
    case SET_COMPANY_SETTINGS:
      return { ...state, companySettings: action.payload };
    case SET_COMPANY_ROLE:
      return { ...state, companyRoles: action.payload };
    case SET_USER_ANALYTICS:
      return { ...state, userAnalytics: action.payload };
    case REFRESH_BRANDS:
      return { ...state, refreshBrands: action.payload };
    case BRAND_FILES_TREE:
      return { ...state, brandFilesTree: action.payload };

    default:
      return state;
  }
}
